import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, CalendarCheck, GraduationCap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { INQUIRE_URL } from "@/constants/publicSite";
import "@fontsource-variable/figtree";
import Features from "./Features";
import "./Home.css";

/** Signed-out cards: each opens the matching part of /features. */
const publicPillars = [
  {
    icon: BookOpen,
    title: "Build courses",
    description: "Units, lessons, readings, key terms and checks, from a standard template.",
    to: "/features#build",
  },
  {
    icon: GraduationCap,
    title: "Teach and grade",
    description: "Questions grade themselves. You step in for discussions and uploads.",
    to: "/features#teach",
  },
  {
    icon: CalendarCheck,
    title: "Run your school",
    description: "Attendance, events with RSVPs, and email to families.",
    to: "/features#run",
  },
];

const Home = () => {
  const { loading, isAuthenticated, hasAccess, session } = useAuth();
  const location = useLocation();
  const homePath = session?.acuityLearn?.defaultHomePath || "/";
  if (!loading && isAuthenticated && hasAccess && homePath !== "/") {
    return <Navigate to={homePath} replace state={location.state} />;
  }

  const signedIn = isAuthenticated && hasAccess;

  if (!signedIn) {
    return (
      <div className="al-home al-home--public">
        <section className="al-home-welcome" aria-labelledby="al-home-title">
          <div className="al-home-welcome-copy">
            <div className="al-home-brandline">
              <span className="al-home-mark" aria-hidden="true">
                <img src="/brand/acuity-mark.png" alt="" />
              </span>
              <span className="al-home-eyebrow">AcuityLearn</span>
            </div>
            <h1 id="al-home-title">Welcome to AcuityLearn</h1>
            <p className="al-home-lead">
              Courses, teaching and student learning, all in one place. Build a course once and watch students move
              through it.
            </p>

            {!isAuthenticated ? (
              <div className="al-home-actions">
                <Link to="/auth" className="al-home-btn al-home-btn--primary">
                  Sign in
                </Link>
                <Link to="/features" className="al-home-btn al-home-btn--brand">
                  See features
                  <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
                </Link>
                <a
                  href={INQUIRE_URL}
                  className="al-home-btn al-home-btn--ghost"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Inquire here
                  <ArrowUpRight size={16} strokeWidth={2} aria-hidden="true" />
                </a>
              </div>
            ) : (
              <div className="al-home-note">
                <p>You do not have access to this platform.</p>
                <a href={INQUIRE_URL} target="_blank" rel="noopener noreferrer">
                  Inquire here
                  <ArrowUpRight size={14} strokeWidth={2} aria-hidden="true" />
                </a>
              </div>
            )}
          </div>

          <div className="al-home-butler">
            <div className="al-home-bubble">
              <p className="al-home-bubble-name">Butler</p>
              <p>Hi, I'm Butler. Want a quick tour of what you can build here?</p>
              <Link to="/features">Show me around</Link>
            </div>
            <img src="/mascot/waving.png" alt="Butler, the AcuityLearn guide, waving hello" />
          </div>
        </section>

        <ul className="al-home-pillars al-home-pillars--public">
          {publicPillars.map((pillar) => (
            <li key={pillar.title}>
              <Link to={pillar.to} className="al-home-pillar al-home-pillar--link">
                <span className="al-home-pillar-icon" aria-hidden="true">
                  <pillar.icon size={20} strokeWidth={2} />
                </span>
                <h2>{pillar.title}</h2>
                <p>{pillar.description}</p>
                <span className="al-home-pillar-more">
                  See how
                  <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Signed-in Home is the features tour, inside the app shell.
  return <Features variant="home" />;
};

export default Home;
